import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWhatsApp } from '../hooks/useWhatsApp'; // Imported the hook

import logoImg from '../uploads/logo.png'; 

import { 
  LayoutDashboard, 
  Send, 
  MessageSquare, 
  LogOut, 
  Menu, 
  X, 
  Wallet,
  Megaphone,
  Settings,
  Shield,
  Inbox,
  Smartphone
} from 'lucide-react';

export default function Layout() {
  const { logout, user } = useAuth();
  const { status } = useWhatsApp(); // Extract status from global hook
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false); // React way to handle image fallback

  const isAdmin = user?.role === 'admin';

  const getLinkClass = (path, isAdminLink = false) => {
    const baseClass = "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group";
    const isActive = location.pathname === path;
    
    if (isAdminLink) {
      return isActive 
        ? `${baseClass} bg-rose-600 text-white shadow-lg shadow-rose-100` 
        : `${baseClass} text-rose-600 hover:bg-rose-50 font-semibold`;
    }

    return isActive 
      ? `${baseClass} bg-indigo-600 text-white shadow-lg shadow-indigo-100 font-semibold` 
      : `${baseClass} text-slate-600 hover:bg-indigo-50 hover:text-indigo-700`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} /> },
    { name: 'Quick Send', path: '/send', icon: <Send size={20} /> },
    { name: 'Campaigns', path: '/campaigns', icon: <Megaphone size={20} /> },
    { name: 'Live Inbox', path: '/inbox', icon: <Inbox size={20} /> },
    { name: 'AI Conversations', path: '/chat', icon: <MessageSquare size={20} /> },
    { name: 'Wallet', path: '/wallet', icon: <Wallet size={20} /> },
    { name: 'Settings', path: '/settings', icon: <Settings size={20} /> },
  ];

  // Helper component for the WA Status pill to keep JSX clean
  const WAStatusBadge = () => {
    const isReady = status === 'ready';
    const isInit = status === 'initializing';
    
    return (
      <div className={`mx-4 mb-4 flex items-center justify-between px-3 py-2 rounded-lg border ${
        isReady ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 
        isInit ? 'bg-amber-50 border-amber-100 text-amber-700' : 
        'bg-rose-50 border-rose-100 text-rose-700'
      }`}>
        <div className="flex items-center gap-2">
          <Smartphone size={16} />
          <span className="text-xs font-bold uppercase tracking-wider">
            {isReady ? 'Connected' : isInit ? 'Starting...' : 'Disconnected'}
          </span>
        </div>
        <div className={`w-2 h-2 rounded-full ${isReady ? 'bg-emerald-500 animate-pulse' : isInit ? 'bg-amber-500 animate-pulse' : 'bg-rose-500'}`} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      
      {/* --- DESKTOP SIDEBAR --- */}
      <aside className="hidden md:flex flex-col w-72 bg-white border-r h-screen fixed left-0 top-0 z-30 shadow-sm">
        
        {/* Brand Header */}
        <div className="h-24 flex items-center px-6 border-b">
          <div className="flex items-center gap-3 p-2 rounded-2xl bg-slate-50 border border-slate-100 shadow-inner w-full">
            {!logoError ? (
              <img 
                src={logoImg} 
                alt="Whatsappbot Logo" 
                className="w-10 h-10 rounded-xl object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-black text-xs">WB</div>
            )}
            <span className="font-black text-xl text-slate-900 tracking-tighter truncate">Whatsappbot</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 space-y-1 overflow-y-auto custom-scrollbar flex flex-col">
          
          {/* Global WhatsApp Status Indicator */}
          <WAStatusBadge />

          <div className="px-4 space-y-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className={getLinkClass(item.path)}>
                {item.icon}
                <span>{item.name}</span>
              </Link>
            ))}
          </div>

          {isAdmin && (
            <div className="px-4">
              <div className="my-6 border-t border-slate-100"></div>
              <p className="mb-3 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Administration</p>
              <Link to="/admin" className={getLinkClass('/admin', true)}>
                <Shield size={20} />
                <span>Control Panel</span>
              </Link>
            </div>
          )}
        </nav>

        {/* User Context Area */}
        <div className="p-4 border-t bg-slate-50/50">
          <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold border-2 border-white shadow-sm shrink-0">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-slate-900 truncate">{user?.username || 'Guest'}</p>
              <p className="text-xs font-bold text-emerald-600">₹{Number(user?.wallet_balance || 0).toFixed(2)}</p>
            </div>
          </div>
          
          <button 
            onClick={handleLogout}
            className="flex items-center justify-center gap-2 w-full px-4 py-3.5 text-sm font-black text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl transition-all active:scale-95"
          >
            <LogOut size={18} /> 
            <span>Terminate Session</span>
          </button>
        </div>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main className="flex-1 md:ml-72 min-h-screen">
        {/* Mobile Nav Header */}
        <div className="md:hidden sticky top-0 bg-white/80 backdrop-blur-md border-b z-40 px-6 h-16 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-2">
                {!logoError && (
                  <img src={logoImg} alt="Logo" className="w-8 h-8 rounded-lg" onError={() => setLogoError(true)} />
                )}
                <span className="font-black text-lg text-slate-900 tracking-tighter">Whatsappbot</span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-slate-600 bg-slate-50 rounded-lg">
                <Menu size={24} />
            </button>
        </div>

        <div className="p-6 md:p-10 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* MOBILE DRAWER */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 w-72 h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-500">
            <div className="h-20 flex items-center px-8 border-b justify-between">
              <span className="font-black text-xl text-indigo-600 italic">Menu</span>
              <X className="text-slate-400" onClick={() => setIsMobileMenuOpen(false)} />
            </div>
            <nav className="py-6 space-y-1 flex-grow overflow-y-auto flex flex-col">
              
              <WAStatusBadge />

              <div className="px-4 space-y-1">
                {navItems.map((item) => (
                  <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={getLinkClass(item.path)}>
                    {item.icon} {item.name}
                  </Link>
                ))}
              </div>

              {/* Added Admin links to mobile menu */}
              {isAdmin && (
                <div className="px-4 mt-6">
                  <div className="border-t border-slate-100 pt-6"></div>
                  <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className={getLinkClass('/admin', true)}>
                    <Shield size={20} />
                    <span>Control Panel</span>
                  </Link>
                </div>
              )}
            </nav>
            <div className="p-4 border-t">
              <button 
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 text-sm font-black text-rose-600 bg-rose-50 rounded-xl"
              >
                <LogOut size={18} /> Logout
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}